/**
 * Debug panel for capturing the current pose of the active bone and a
 * configurable number of its ancestors.
 *
 * Captures persist in localStorage so they survive page reloads. Each capture
 * stores bone-chain quaternions in a human-readable JSON format designed to be
 * pasted directly into a conversation for analysis and comparison.
 *
 * Toggle from Debug menu → "Pose Capture" (pose mode only).
 */

import { useCallback, useMemo, useState } from "react";
import type * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import { getBone, getRestPose } from "../mannequin/boneRegistry.ts";
import { getCorrectiveDefinitions } from "../mannequin/correctiveShapeKeys.ts";
import { type Quat4, qmul } from "../mannequin/quaternionMath.ts";
import { TWIST_BONE_NAMES } from "../mannequin/twistBones.ts";
import { CopyButton } from "../ui/CopyButton.tsx";
import { DraggablePanel } from "../ui/DraggablePanel.tsx";
import { usePanelSelection } from "../ui/floatingPanelStore.ts";
import {
  COLOR_DEBUG_HIGHLIGHT_BG,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  Z_DEBUG,
} from "../ui/tokens.ts";

// ---------------------------------------------------------------------------
// Capture data model
// ---------------------------------------------------------------------------

interface BoneEntry {
  bone: string;
  /** Current absolute quaternion [x, y, z, w]. */
  pose: Quat4;
  /** Rest-pose quaternion [x, y, z, w]. */
  rest: Quat4;
  /** Depth from the target bone (0 = target, 1 = parent, …). */
  depth: number;
}

interface PoseCapture {
  id: string;
  /** ISO-8601 timestamp. */
  timestamp: string;
  /** User-provided or auto-generated label. */
  label: string;
  /** The bone that was active when captured. */
  targetBone: string;
  /** How many parent levels were included. */
  parentDepth: number;
  /** Model ID of the mannequin (e.g. "male-neutral"). */
  modelId?: string;
  /** Ordered bone chain: target first, then parents. */
  bones: BoneEntry[];
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const STORAGE_KEY = "poseref-debug-pose-captures";

function loadCaptures(): PoseCapture[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCaptures(captures: PoseCapture[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(captures));
  } catch {
    /* quota exceeded — silently drop */
  }
}

// ---------------------------------------------------------------------------
// Serialise a capture for clipboard (analysis-friendly)
// ---------------------------------------------------------------------------

function serialiseCapture(c: PoseCapture): string {
  const bones = c.bones.map((b) => ({
    bone: b.bone,
    depth: b.depth,
    pose: b.pose.map((v) => +v.toFixed(6)),
    rest: b.rest.map((v) => +v.toFixed(6)),
  }));
  return JSON.stringify(
    {
      label: c.label,
      timestamp: c.timestamp,
      targetBone: c.targetBone,
      parentDepth: c.parentDepth,
      modelId: c.modelId,
      bones,
    },
    null,
    2,
  );
}

function serialiseAll(captures: PoseCapture[]): string {
  return JSON.stringify(
    captures.map((c) => ({
      label: c.label,
      timestamp: c.timestamp,
      targetBone: c.targetBone,
      parentDepth: c.parentDepth,
      modelId: c.modelId,
      bones: c.bones.map((b) => ({
        bone: b.bone,
        depth: b.depth,
        pose: b.pose.map((v) => +v.toFixed(6)),
        rest: b.rest.map((v) => +v.toFixed(6)),
      })),
    })),
    null,
    2,
  );
}

// ---------------------------------------------------------------------------
// Build bone chain from active bone + N parents
// ---------------------------------------------------------------------------

function buildBoneChain(
  objectId: string,
  boneName: string,
  parentDepth: number,
): BoneEntry[] | null {
  const bonePoses = useStore.getState().mannequinBonePoses.get(objectId);
  const entries: BoneEntry[] = [];

  let current = getBone(objectId, boneName);
  let depth = 0;

  while (current && depth <= parentDepth) {
    const name = current.name;
    // Skip twist bones — they're auto-derived, not user-posed
    if (!TWIST_BONE_NAMES.has(name)) {
      const pose =
        bonePoses?.get(name) ??
        ([
          current.quaternion.x,
          current.quaternion.y,
          current.quaternion.z,
          current.quaternion.w,
        ] as Quat4);
      const rest = getRestPose(objectId, name) ?? ([0, 0, 0, 1] as Quat4);
      entries.push({ bone: name, pose, rest, depth });
    }
    // Walk up — skip non-Bone parents (e.g. the skeleton root)
    const parent = current.parent;
    if (!parent || !("isBone" in parent) || !(parent as any).isBone) break;
    current = parent as THREE.Bone;
    depth++;
  }

  return entries.length > 0 ? entries : null;
}

// ---------------------------------------------------------------------------
// Timestamp formatting (compact, local)
// ---------------------------------------------------------------------------

function shortTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

// ---------------------------------------------------------------------------
// Pose capture analysis — cross-reference against corrective bindings
// ---------------------------------------------------------------------------

interface AnalysisFinding {
  /** defKey: joint:action */
  key: string;
  joint: string;
  action: string;
  bone: string;
  /** Expected activation weight (0–1). */
  expectedWeight: number;
  /** Activation angle of the corrective (radians). */
  activationAngle: number;
  /** Whether the morph target exists in the GLB. */
  hasMorph: boolean;
}

/** Quaternion geodesic angle between two quaternions (radians). */
function qAngle(a: Quat4, b: Quat4): number {
  const dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];
  return 2 * Math.acos(Math.min(1, Math.abs(dot)));
}

/** Mirror a quaternion for R-side bones (negate Y, Z). */
function mirrorQuat(q: Quat4): Quat4 {
  return [q[0], -q[1], -q[2], q[3]];
}

/** Invert (conjugate) a unit quaternion. */
function qInv(q: Quat4): Quat4 {
  return [-q[0], -q[1], -q[2], q[3]];
}

/**
 * Analyze a pose capture against all corrective definitions.
 * Returns findings sorted by expected weight (highest first).
 */
function analyzeCapture(capture: PoseCapture): AnalysisFinding[] {
  const defs = getCorrectiveDefinitions();
  const snap = window.__poseref_correctiveDebug;

  // Build set of defKeys that have morph targets in the GLB
  const boundDefs = new Set<string>();
  if (snap) {
    for (const b of snap.bindings) {
      const parts = b.groupKey.split(":");
      if (parts[0] !== "combo") boundDefs.add(`${parts[0]}:${parts[2]}`);
    }
  }

  // Index captured bones by name for quick lookup
  const boneByName = new Map<string, BoneEntry>();
  for (const b of capture.bones) boneByName.set(b.bone, b);

  const findings: AnalysisFinding[] = [];

  for (const def of defs) {
    // Check both sided variants and unsided
    const variants: Array<{ boneName: string; target: Quat4 }> = [];
    if (def.unsided) {
      variants.push({ boneName: def.bone, target: def.targetRotation });
    } else {
      variants.push({ boneName: def.bone + "L", target: def.targetRotation });
      variants.push({
        boneName: def.bone + "R",
        target: mirrorQuat(def.targetRotation),
      });
    }

    for (const { boneName, target } of variants) {
      const entry = boneByName.get(boneName);
      if (!entry) continue;

      // delta = inv(rest) * pose
      const delta = qmul(qInv(entry.rest), entry.pose);
      const dist = qAngle(delta, target);
      const progress = Math.max(0, def.activationAngle - dist);
      const weight =
        def.activationAngle > 0
          ? Math.min(1, progress / def.activationAngle)
          : 0;

      if (weight > 0.01) {
        findings.push({
          key: `${def.joint}:${def.action}`,
          joint: def.joint,
          action: def.action,
          bone: boneName,
          expectedWeight: weight,
          activationAngle: def.activationAngle,
          hasMorph: boundDefs.has(`${def.joint}:${def.action}`),
        });
      }
    }
  }

  // Deduplicate: keep highest weight per key
  const best = new Map<string, AnalysisFinding>();
  for (const f of findings) {
    const prev = best.get(f.key);
    if (!prev || f.expectedWeight > prev.expectedWeight) best.set(f.key, f);
  }

  return [...best.values()].sort((a, b) => b.expectedWeight - a.expectedWeight);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PoseCaptureDebugPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.poseCapture);
  const poseMode = useStore((s) => s.poseMode);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const setBonePoses = useStore((s) => s.setBonePoses);
  const sel = usePanelSelection("debug-pose-capture", () =>
    togglePanel("poseCapture"),
  );

  const [captures, setCaptures] = useState<PoseCapture[]>(loadCaptures);
  const [parentDepth, setParentDepth] = useState(2);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const activeBone = poseMode?.activeBone ?? null;
  const objectId = poseMode?.objectId ?? null;

  // Persist whenever captures change
  const updateCaptures = useCallback((next: PoseCapture[]) => {
    setCaptures(next);
    saveCaptures(next);
  }, []);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const handleCapture = useCallback(() => {
    if (!objectId || !activeBone) return;
    const chain = buildBoneChain(objectId, activeBone, parentDepth);
    if (!chain) return;

    const state = useStore.getState();
    const obj = state.sceneObjects.get(objectId);

    const capture: PoseCapture = {
      id: `cap_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      timestamp: new Date().toISOString(),
      label: `${activeBone} +${parentDepth}`,
      targetBone: activeBone,
      parentDepth,
      modelId: obj?.mannequinModelId ?? undefined,
      bones: chain,
    };

    updateCaptures([capture, ...captures]);
  }, [objectId, activeBone, parentDepth, captures, updateCaptures]);

  const handleDelete = useCallback(
    (id: string) => {
      updateCaptures(captures.filter((c) => c.id !== id));
      if (expandedId === id) setExpandedId(null);
    },
    [captures, expandedId, updateCaptures],
  );

  const handleClearAll = useCallback(() => {
    updateCaptures([]);
    setExpandedId(null);
  }, [updateCaptures]);

  const handleApply = useCallback(
    (capture: PoseCapture) => {
      if (!objectId) return;
      const poses = new Map<string, Quat4>();
      for (const b of capture.bones) poses.set(b.bone, b.pose);
      setBonePoses(objectId, poses);
    },
    [objectId, setBonePoses],
  );

  const getCopyAllText = useCallback(() => serialiseAll(captures), [captures]);

  // Memoised label for the capture button
  const captureLabel = useMemo(() => {
    if (!activeBone) return "no bone selected";
    return `${activeBone} +${parentDepth} parent${parentDepth !== 1 ? "s" : ""}`;
  }, [activeBone, parentDepth]);

  // -------------------------------------------------------------------------
  // Gate
  // -------------------------------------------------------------------------

  if (!debugEnabled || !panelOpen || !poseMode) return null;

  return (
    <DraggablePanel
      id="debug-pose-capture"
      defaultPosition={{ bottom: 8, right: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      selectionColor="#0f0"
      {...sel}
      style={{ width: 300 }}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "monospace",
            fontSize: 10,
          }}
        >
          <span style={{ color: "#0f0", fontWeight: "bold" }}>
            POSE CAPTURE
          </span>
          {captures.length > 0 && <CopyButton getText={getCopyAllText} />}
        </div>
      }
    >
      <div
        style={{
          background: "rgba(0,0,0,0.85)",
          fontFamily: "monospace",
          fontSize: 10,
          borderRadius: "0 0 6px 6px",
          pointerEvents: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Controls */}
        <div
          style={{
            padding: "6px 8px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {/* Parent depth */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ color: COLOR_TEXT_DIM, whiteSpace: "nowrap" }}>
              Parents:
            </label>
            <input
              data-no-drag
              type="range"
              min={0}
              max={10}
              value={parentDepth}
              onChange={(e) => setParentDepth(+e.target.value)}
              style={{ flex: 1, accentColor: "#0f0", height: 12 }}
            />
            <span style={{ color: "#0f0", width: 16, textAlign: "right" }}>
              {parentDepth}
            </span>
          </div>

          {/* Capture button */}
          <button
            data-no-drag
            disabled={!activeBone}
            onClick={handleCapture}
            style={{
              width: "100%",
              padding: "5px 8px",
              border: `1px solid ${activeBone ? "rgba(0,255,0,0.4)" : "rgba(255,255,255,0.1)"}`,
              borderRadius: 4,
              background: activeBone ? COLOR_DEBUG_HIGHLIGHT_BG : "transparent",
              color: activeBone ? "#0f0" : COLOR_TEXT_FAINT,
              fontFamily: "monospace",
              fontSize: 10,
              cursor: activeBone ? "pointer" : "default",
              textAlign: "left",
            }}
          >
            {activeBone ? "\u25CF " : ""}
            {captureLabel}
          </button>
        </div>

        {/* Capture list */}
        {captures.length > 0 && (
          <div
            style={{
              maxHeight: 320,
              overflowY: "auto",
              borderTop: "1px solid #333",
            }}
          >
            {captures.map((cap) => (
              <CaptureRow
                key={cap.id}
                capture={cap}
                expanded={expandedId === cap.id}
                onToggleExpand={() =>
                  setExpandedId(expandedId === cap.id ? null : cap.id)
                }
                onDelete={() => handleDelete(cap.id)}
                onApply={() => handleApply(cap)}
              />
            ))}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: COLOR_TEXT_DIM,
            borderTop: "1px solid #333",
            padding: "4px 8px",
            fontSize: 9,
          }}
        >
          <span>
            {captures.length} capture{captures.length !== 1 ? "s" : ""}
          </span>
          {captures.length > 0 && (
            <button
              data-no-drag
              onClick={handleClearAll}
              style={{
                border: "none",
                background: "transparent",
                color: "#e88",
                fontFamily: "monospace",
                fontSize: 9,
                cursor: "pointer",
                padding: "0 2px",
              }}
            >
              clear all
            </button>
          )}
        </div>
      </div>
    </DraggablePanel>
  );
}

// ---------------------------------------------------------------------------
// Capture row
// ---------------------------------------------------------------------------

function CaptureRow({
  capture,
  expanded,
  onToggleExpand,
  onDelete,
  onApply,
}: {
  capture: PoseCapture;
  expanded: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
  onApply: () => void;
}) {
  const [analysis, setAnalysis] = useState<AnalysisFinding[] | null>(null);

  const handleAnalyze = useCallback(() => {
    setAnalysis((prev) => (prev ? null : analyzeCapture(capture)));
  }, [capture]);

  const getCopyText = useCallback(() => serialiseCapture(capture), [capture]);

  return (
    <div
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.04)",
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 8px",
        }}
      >
        <button
          data-no-drag
          onClick={onToggleExpand}
          style={{
            flex: 1,
            border: "none",
            background: "transparent",
            color: "#ccc",
            fontFamily: "monospace",
            fontSize: 10,
            cursor: "pointer",
            textAlign: "left",
            padding: 0,
            display: "flex",
            alignItems: "center",
            gap: 4,
            overflow: "hidden",
          }}
        >
          <span
            style={{
              fontSize: 8,
              width: 10,
              flexShrink: 0,
              color: COLOR_TEXT_FAINT,
            }}
          >
            {expanded ? "\u25BC" : "\u25B6"}
          </span>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "#0f0",
            }}
          >
            {capture.label}
          </span>
          <span
            style={{
              color: COLOR_TEXT_FAINT,
              flexShrink: 0,
              marginLeft: "auto",
            }}
          >
            {shortTime(capture.timestamp)}
          </span>
        </button>

        {/* Action buttons */}
        <button
          data-no-drag
          onClick={handleAnalyze}
          title="Analyze corrective coverage"
          style={{
            ...actionBtnStyle,
            color: analysis ? "#0ff" : actionBtnStyle.color,
          }}
        >
          \u2261
        </button>
        <button
          data-no-drag
          onClick={onApply}
          title="Apply this pose"
          style={actionBtnStyle}
        >
          \u21BB
        </button>
        <CopyButton getText={getCopyText} />
        <button
          data-no-drag
          onClick={onDelete}
          title="Delete capture"
          style={{ ...actionBtnStyle, color: "#e88" }}
        >
          \u2715
        </button>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div
          style={{
            padding: "2px 8px 6px 24px",
            color: COLOR_TEXT_DIM,
            fontSize: 9,
            lineHeight: 1.6,
            whiteSpace: "pre",
          }}
        >
          {capture.bones.map((b) => {
            const indent = "  ".repeat(b.depth);
            const q = b.pose.map((v) => v.toFixed(4)).join(", ");
            return (
              <div key={b.bone}>
                <span style={{ color: COLOR_TEXT_FAINT }}>{indent}</span>
                <span style={{ color: b.depth === 0 ? "#0f0" : "#ccc" }}>
                  {b.bone}
                </span>
                <span style={{ color: COLOR_TEXT_FAINT }}> [{q}]</span>
              </div>
            );
          })}
          {capture.modelId && (
            <div style={{ color: COLOR_TEXT_FAINT, marginTop: 2 }}>
              model: {capture.modelId}
            </div>
          )}
        </div>
      )}

      {/* Analysis findings */}
      {analysis && (
        <div
          style={{
            padding: "4px 8px 6px",
            borderTop: "1px solid rgba(0,255,255,0.15)",
            fontSize: 9,
            lineHeight: 1.7,
          }}
        >
          <div style={{ color: "#0ff", fontWeight: "bold", marginBottom: 2 }}>
            CORRECTIVE ANALYSIS
          </div>
          {analysis.length === 0 ? (
            <div style={{ color: COLOR_TEXT_FAINT }}>
              No correctives expected for this pose.
            </div>
          ) : (
            analysis.map((f) => {
              const pct = (f.expectedWeight * 100).toFixed(0);
              return (
                <div
                  key={f.key + f.bone}
                  style={{ display: "flex", alignItems: "center", gap: 4 }}
                >
                  <span
                    style={{
                      color: f.hasMorph
                        ? f.expectedWeight >= 0.5
                          ? "#0f0"
                          : "#ff0"
                        : "#e88",
                      width: 32,
                      textAlign: "right",
                      flexShrink: 0,
                    }}
                  >
                    {pct}%
                  </span>
                  <span
                    style={{
                      color: "#ccc",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f.joint}:{f.action}
                  </span>
                  <span style={{ color: COLOR_TEXT_FAINT, flexShrink: 0 }}>
                    {f.bone}
                  </span>
                  {!f.hasMorph && (
                    <span style={{ color: "#e88", flexShrink: 0, fontSize: 8 }}>
                      no morph
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const actionBtnStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: COLOR_TEXT_DIM,
  fontFamily: "monospace",
  fontSize: 10,
  cursor: "pointer",
  padding: "0 2px",
  flexShrink: 0,
};
